// =====================================================================
// Disposition de l'arbre
// =====================================================================
// Ce fichier vit dans `partage/` parce qu'il sert DES DEUX CÔTÉS : le
// navigateur en tire le SVG, le serveur les vecteurs du livre imprimé. Un
// seul algorithme, deux rendus — sans ce partage, les deux finiraient par
// diverger et l'arbre imprimé ne ressemblerait plus à celui qu'on a vu.
// =====================================================================
// Un arbre généalogique n'est pas un arbre au sens informatique : un
// enfant a deux parents, les fratries doivent rester côte à côte, et le
// graphe comporte des cycles non orientés (deux cousins remontent au même
// aïeul par deux chemins). On ne peut donc pas utiliser une disposition
// d'arbre classique.
//
// LES COUPLES SONT L'UNITÉ DE BASE, pas les personnes. Sans cela, les
// deux parents d'un même enfant sont placés indépendamment : ils se
// retrouvent côte à côte sous un aïeul dont un seul descend, et la mère
// paraît être une fille de ce grand-père. Deux traits séparés rejoignent
// alors l'enfant, ce qui se lit comme deux filiations sans rapport.
//
// On place donc des UNITÉS : un couple compte pour une, ses deux membres
// sont accolés et reliés par une barre horizontale, et une seule ligne
// descend du milieu de cette barre vers leurs enfants.
//
// La disposition procède ensuite par couches, comme un diagramme de
// Sugiyama simplifié :
//
//   1. chaque personne appartient à une GÉNÉRATION, qui se déduit de la
//      relation déjà calculée par la base : génération = dx − dy, où dx
//      est ma distance à l'ancêtre commun et dy la sienne ;
//   2. à l'intérieur d'une génération, on place chaque unité au
//      barycentre de ses parents, puis de ses enfants, en alternant, et
//      on écarte ce qui se chevauche.
// =====================================================================

import type { Lien, Parent } from './types';

export const LARGEUR_NOEUD = 112;
export const HAUTEUR_NOEUD = 98;
export const DIAMETRE_AVATAR = 58;
/** Entre les deux membres d'un couple : serré, ils forment un bloc. */
const ECART_COUPLE = 14;
/** Entre deux unités distinctes : plus large, pour qu'on les distingue. */
const ECART_HORIZONTAL = 34;
const ECART_VERTICAL = 148;
const PASSES = 12;

export interface NoeudArbre {
  id: string;
  nom: string | null;
  prenoms: string | null;
  sexe: 'M' | 'F' | null;
  libelle: string;
  decede: boolean;
  village: string | null;
  /** 0 = moi, positif vers les ancêtres, négatif vers les descendants. */
  generation: number;
  moi: boolean;
  /** Vraie pour une personne saisie hors ligne, pas encore envoyée. */
  enAttente: boolean;
  x: number;
  y: number;
}

/** Le trait qui descend vers un enfant, depuis un parent ou un couple. */
export interface AreteArbre {
  /** Point d'attache : le milieu de la barre de couple, ou le bas du
   *  bloc d'un parent isolé. */
  depart: { x: number; y: number };
  /** Hauteur du bas des blocs parents. Le segment horizontal passe en
   *  dessous, pour ne jamais traverser les noms. */
  basParent: number;
  enfant: NoeudArbre;
}

/** La barre horizontale qui unit les deux membres d'un couple. */
export interface BarreCouple {
  x1: number;
  x2: number;
  y: number;
}

export interface Disposition {
  noeuds: NoeudArbre[];
  aretes: AreteArbre[];
  barres: BarreCouple[];
  largeur: number;
  hauteur: number;
}

interface Moi {
  personne_id: string;
  nom: string | null;
  prenoms: string | null;
  sexe: 'M' | 'F' | null;
}

interface Unite {
  cle: string;
  membres: NoeudArbre[];
  generation: number;
  x: number;
  largeur: number;
}

export function disposer(
  moi: Moi,
  parents: Parent[],
  liens: Lien[],
): Disposition {
  // --- 1. Les nœuds, avec leur génération ----------------------------
  const noeuds = new Map<string, NoeudArbre>();

  noeuds.set(moi.personne_id, {
    id: moi.personne_id,
    nom: moi.nom,
    prenoms: moi.prenoms,
    sexe: moi.sexe,
    libelle: 'moi',
    decede: false,
    village: null,
    generation: 0,
    moi: true,
    enAttente: false,
    x: 0,
    y: 0,
  });

  for (const p of parents) {
    noeuds.set(p.personne_id, {
      id: p.personne_id,
      nom: p.nom,
      prenoms: p.prenoms,
      sexe: p.sexe,
      libelle: p.libelle,
      decede: p.decede,
      village: p.village_origine,
      generation: p.profondeur_moi - p.profondeur_autre,
      moi: false,
      enAttente: 'en_attente' in p,
      x: 0,
      y: 0,
    });
  }

  // --- 2. Filiations restreintes aux nœuds visibles ------------------
  const parentsDe = new Map<string, NoeudArbre[]>();
  const enfantsDe = new Map<string, NoeudArbre[]>();

  for (const l of liens) {
    const parent = noeuds.get(l.parent_id);
    const enfant = noeuds.get(l.enfant_id);
    if (!parent || !enfant) continue;
    pousser(parentsDe, enfant.id, parent);
    pousser(enfantsDe, parent.id, enfant);
  }

  // --- 3. Les couples -------------------------------------------------
  // Deux personnes forment un couple dès qu'un enfant les déclare toutes
  // les deux. On compte leurs enfants communs : en cas de polygamie, une
  // personne ne peut être accolée qu'à un seul conjoint, et c'est celui
  // avec qui elle a le plus d'enfants qui l'emporte. Les autres unions
  // restent dessinées par des traits séparés — il n'existe pas de mise en
  // page plane qui place une personne à côté de trois conjoints à la fois.
  const couples = new Map<string, { membres: [NoeudArbre, NoeudArbre]; enfants: number }>();

  for (const [enfantId, deuxParents] of parentsDe) {
    if (deuxParents.length < 2 || !noeuds.has(enfantId)) continue;
    const [a, b] = [...deuxParents].sort((x, y) => (x.id < y.id ? -1 : 1));
    const cle = `${a.id}~${b.id}`;
    const existant = couples.get(cle);
    if (existant) existant.enfants += 1;
    else couples.set(cle, { membres: [a, b], enfants: 1 });
  }

  // Chaque personne rejoint au plus un couple : le plus fourni.
  const coupleDe = new Map<string, string>();
  const retenus = [...couples.entries()].sort((u, v) => v[1].enfants - u[1].enfants);

  for (const [cle, couple] of retenus) {
    if (couple.membres.some((m) => coupleDe.has(m.id))) continue;
    for (const m of couple.membres) coupleDe.set(m.id, cle);
  }

  // --- 4. Les unités à placer -----------------------------------------
  const unites = new Map<string, Unite>();
  const uniteDe = new Map<string, Unite>();

  for (const n of noeuds.values()) {
    const cle = coupleDe.get(n.id) ?? n.id;
    let unite = unites.get(cle);
    if (!unite) {
      unite = { cle, membres: [], generation: n.generation, x: 0, largeur: 0 };
      unites.set(cle, unite);
    }
    unite.membres.push(n);
    // Un couple occupe une seule ligne : on retient la génération la plus
    // ancienne de ses membres.
    unite.generation = Math.max(unite.generation, n.generation);
    uniteDe.set(n.id, unite);
  }

  for (const u of unites.values()) {
    // Le membre lié par le sang en premier : il porte la relation, et
    // c'est lui qu'on souhaite voir du côté de sa propre lignée.
    u.membres.sort((a, b) => (parentsDe.has(b.id) ? 1 : 0) - (parentsDe.has(a.id) ? 1 : 0));
    u.largeur =
      u.membres.length * LARGEUR_NOEUD + (u.membres.length - 1) * ECART_COUPLE;
  }

  // Le membre ANCRÉ d'une unité : celui dont on connaît les parents.
  // C'est lui qui doit tomber sous son propre père, et non le milieu du
  // couple — sinon le grand-père paraît être le père des deux conjoints.
  const ancre = new Map<string, NoeudArbre>();
  for (const u of unites.values()) {
    const lie = u.membres.find((m) => (parentsDe.get(m.id) ?? []).length > 0);
    if (lie) ancre.set(u.cle, lie);
  }

  // Relations entre unités, pour les barycentres. On retient les
  // PERSONNES voisines, pas leurs unités : c'est la position réelle du
  // parent ou de l'enfant qui doit attirer, pas le centre de son couple.
  const parentsVoisins = new Map<string, NoeudArbre[]>();
  const enfantsVoisins = new Map<string, NoeudArbre[]>();

  for (const u of unites.values()) {
    for (const m of u.membres) {
      for (const p of parentsDe.get(m.id) ?? []) {
        if (uniteDe.get(p.id) !== u) pousser(parentsVoisins, u.cle, p);
      }
      for (const e of enfantsDe.get(m.id) ?? []) {
        if (uniteDe.get(e.id) !== u) pousser(enfantsVoisins, u.cle, e);
      }
    }
  }

  const unitesParentes = new Map<string, Unite[]>();
  for (const [cle, liste] of parentsVoisins) {
    for (const p of liste) {
      const up = uniteDe.get(p.id);
      if (up) pousser(unitesParentes, cle, up);
    }
  }

  // --- 5. Les couches --------------------------------------------------
  const couches = new Map<number, Unite[]>();
  for (const u of unites.values()) pousser(couches, u.generation, u);

  const generations = [...couches.keys()].sort((a, b) => b - a);

  generations.forEach((g, i) => {
    for (const u of couches.get(g)!) {
      for (const m of u.membres) m.y = i * ECART_VERTICAL;
    }
  });

  for (const g of generations) {
    const couche = couches.get(g)!;
    couche.sort((a, b) => {
      const ca = cleFratrie(unitesParentes.get(a.cle));
      const cb = cleFratrie(unitesParentes.get(b.cle));
      if (ca !== cb) return ca < cb ? -1 : 1;
      return nomDe(a.membres[0]).localeCompare(nomDe(b.membres[0]), 'fr');
    });
    let x = 0;
    for (const u of couche) {
      u.x = x;
      x += u.largeur + ECART_HORIZONTAL;
    }
  }

  // --- 6. Barycentres alternés, puis écartement ------------------------
  for (let passe = 0; passe < PASSES; passe++) {
    const versLeBas = passe % 2 === 0;
    const ordre = versLeBas ? generations : [...generations].reverse();

    for (const g of ordre) {
      const couche = couches.get(g)!;

      for (const u of couche) {
        const voisins = versLeBas
          ? (parentsVoisins.get(u.cle) ?? [])
          : (enfantsVoisins.get(u.cle) ?? []);
        if (voisins.length === 0) continue;

        const centre =
          voisins.reduce((s, v) => s + v.x + LARGEUR_NOEUD / 2, 0) / voisins.length;

        if (versLeBas) {
          // Positionné par ses parents : c'est le membre ancré qui doit
          // tomber sous eux, pas le milieu du couple.
          const lie = ancre.get(u.cle) ?? u.membres[0];
          const rang = u.membres.indexOf(lie);
          u.x = centre - LARGEUR_NOEUD / 2 - rang * (LARGEUR_NOEUD + ECART_COUPLE);
        } else {
          // Positionné par ses enfants : ils descendent du milieu du
          // couple, donc c'est le milieu qui s'aligne sur eux.
          u.x = centre - u.largeur / 2;
        }
      }

      ecarter(couche);

      // Recopier aussitôt la position des unités sur leurs membres. Le
      // barycentre lit la position des PERSONNES ; sans cette
      // synchronisation, chaque passe calcule à partir de coordonnées
      // périmées et la disposition ne converge jamais.
      for (const u of couche) placerMembres(u);
    }
  }

  // --- 7. Recentrage ----------------------------------------------------
  for (const u of unites.values()) placerMembres(u);

  const xs = [...noeuds.values()].map((n) => n.x);
  const minX = Math.min(...xs);
  for (const n of noeuds.values()) n.x -= minX;
  for (const u of unites.values()) u.x -= minX;

  // --- 8. Traits de descendance et barres de couple --------------------
  const barres: BarreCouple[] = [];
  for (const u of unites.values()) {
    if (u.membres.length < 2) continue;
    const [a, b] = u.membres;
    barres.push({
      x1: Math.min(a.x, b.x) + LARGEUR_NOEUD / 2,
      x2: Math.max(a.x, b.x) + LARGEUR_NOEUD / 2,
      y: a.y + DIAMETRE_AVATAR / 2,
    });
  }

  // Un seul trait par enfant quand ses deux parents forment un couple :
  // il part du milieu de leur barre. Sinon un trait par parent connu.
  const aretes: AreteArbre[] = [];
  for (const enfant of noeuds.values()) {
    const sesParents = parentsDe.get(enfant.id) ?? [];
    if (sesParents.length === 0) continue;

    const coupleCle =
      sesParents.length === 2 &&
      coupleDe.get(sesParents[0].id) &&
      coupleDe.get(sesParents[0].id) === coupleDe.get(sesParents[1].id)
        ? coupleDe.get(sesParents[0].id)
        : null;

    if (coupleCle) {
      // Le trait part de la BARRE elle-même, pas du vide sous les noms :
      // c'est l'union qui engendre l'enfant, et le dessin doit le dire.
      const u = unites.get(coupleCle)!;
      const haut = u.membres[0].y;
      aretes.push({
        depart: { x: u.x + u.largeur / 2, y: haut + DIAMETRE_AVATAR / 2 },
        basParent: haut + HAUTEUR_NOEUD,
        enfant,
      });
    } else {
      for (const p of sesParents) {
        aretes.push({
          depart: { x: p.x + LARGEUR_NOEUD / 2, y: p.y + HAUTEUR_NOEUD },
          basParent: p.y + HAUTEUR_NOEUD,
          enfant,
        });
      }
    }
  }

  const maxX = Math.max(...[...noeuds.values()].map((n) => n.x), 0);
  const maxY = Math.max(...[...noeuds.values()].map((n) => n.y), 0);

  return {
    noeuds: [...noeuds.values()],
    aretes,
    barres,
    largeur: maxX + LARGEUR_NOEUD,
    hauteur: maxY + HAUTEUR_NOEUD,
  };
}

/** Reporte la position d'une unité sur chacun de ses membres. */
function placerMembres(u: Unite): void {
  let x = u.x;
  for (const m of u.membres) {
    m.x = x;
    x += LARGEUR_NOEUD + ECART_COUPLE;
  }
}

/** Écarte les unités d'une couche pour qu'aucune ne se chevauche. */
function ecarter(couche: Unite[]): void {
  couche.sort((a, b) => a.x - b.x);
  for (let i = 1; i < couche.length; i++) {
    const minimum = couche[i - 1].x + couche[i - 1].largeur + ECART_HORIZONTAL;
    if (couche[i].x < minimum) couche[i].x = minimum;
  }
}

/** Les unités qui ont les mêmes parents partagent cette clé. */
function cleFratrie(parentes: Unite[] | undefined): string {
  if (!parentes || parentes.length === 0) return '￿';
  return parentes
    .map((u) => u.cle)
    .sort()
    .join('|');
}

function nomDe(n: NoeudArbre): string {
  return [n.prenoms, n.nom].filter(Boolean).join(' ') || '~';
}

function pousser<K, V>(carte: Map<K, V[]>, cle: K, valeur: V): void {
  const liste = carte.get(cle);
  if (liste) {
    if (!liste.includes(valeur)) liste.push(valeur);
  } else carte.set(cle, [valeur]);
}
